import { addMetadataFromBase64DataURI, getMetadata } from 'meta-png';

import { SignatureType } from './Signature';

export const PEN_FOR_LIGHT_MODE = {
  r: 71,
  g: 76,
  b: 98
};

export const PEN_FOR_DARK_MODE = {
  r: 255,
  g: 255,
  b: 255
};

export const isDarkMode = () => {
  return document.documentElement.classList.contains('dark');
};

export const getCanvasPenColor = () => {
  return isDarkMode() ? PEN_FOR_DARK_MODE : PEN_FOR_LIGHT_MODE;
};

export const getCanvasPenColorRgb = () => {
  return isDarkMode()
    ? `rgb(${PEN_FOR_DARK_MODE.r}, ${PEN_FOR_DARK_MODE.g}, ${PEN_FOR_DARK_MODE.b})`
    : `rgb(${PEN_FOR_LIGHT_MODE.r}, ${PEN_FOR_LIGHT_MODE.g}, ${PEN_FOR_LIGHT_MODE.b})`;
};

const base64ToUint8Array = (base64: string): Uint8Array<ArrayBufferLike> => {
  // Remove the data URL prefix if present
  const base64Data = base64.split(',')[1] || base64;
  const binaryString = atob(base64Data);

  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
};

export const changeColorOfCanvas = (
  canvas: HTMLCanvasElement | null,
  color: { r: number; g: number; b: number }
): HTMLCanvasElement | null => {
  const ctx = canvas?.getContext('2d');
  if (canvas && ctx) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];

      if (alpha > 0) {
        data[i] = color.r; // Red
        data[i + 1] = color.g; // Green
        data[i + 2] = color.b; // Blue
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  return canvas;
};

export const changeColorOfImage = (
  signature: SignatureType,
  color: { r: number; g: number; b: number }
): Promise<SignatureType> => {
  return new Promise(resolve => {
    if (!signature) {
      resolve(null);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(signature);
      return;
    }

    const img = new Image();
    img.onload = () => {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const canvasModified = changeColorOfCanvas(canvas, color);
        resolve((canvasModified?.toDataURL() || canvas.toDataURL()) as SignatureType);
      } catch (error) {
        console.error('Error changing color of image:', error);
        resolve(signature);
      }
    };

    img.onerror = () => {
      resolve(signature);
    };

    img.src = signature;
  });
};

const MANUAL_UPLOAD_IMAGE_METADATA_KEY = 'isUploadedManually';

export const addUploadMetadata = (base64: SignatureType): SignatureType => {
  try {
    return addMetadataFromBase64DataURI(
      base64 || '',
      MANUAL_UPLOAD_IMAGE_METADATA_KEY,
      'true'
    ) as SignatureType;
  } catch (error) {
    return base64;
  }
};

export const isImageManualyUploaded = (base64: SignatureType): boolean => {
  try {
    return !!getMetadata(base64ToUint8Array(base64 || ''), MANUAL_UPLOAD_IMAGE_METADATA_KEY);
  } catch (error) {
    return false;
  }
};
